import { FC, useState } from 'react'
import {
  Alert,
  AlertDescription,
  AlertIcon,
  AlertTitle,
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Input,
  InputGroup,
  PinInput,
  PinInputField,
  Text,
  useToast
} from '@chakra-ui/react'
import { bgImage, LogoIcon } from '@bemap/ui'
import { userAskResetPassword, userResetChangePassword, verify } from './reset-password-service'
import { IChangePassword } from '@bemap/utils'

const ResetPassword: FC = () => {
  const toast = useToast();
  const [loading, setLoading] = useState<boolean>(false);
  const [step, setStep] = useState<number>(0);
  const [form, setForm] = useState({
    email: '',
    otp: ''
  });

  const initialPassword = {
    old: '',
    new: '',
    confirm_new: '',
  };

  const errorMessages = {
    noError: '',
    empty: 'Please fill in all fields',
    mismatch: 'Passwords do not match',
    tooSmall: 'Password must be at least 6 characters long'
  }

  const isErrorInitial = {
    old: false,
    new: false,
    confirm_new: false,
  }

  const [password, setPassword] = useState<IChangePassword>(initialPassword);
  const [error, setError] = useState('')
  const [isError, setIsError] = useState(isErrorInitial)

  const askReset = async () => {
    try {
      setLoading(true);
      await userAskResetPassword(form.email);
      setLoading(false);
      setStep(1);
      toast({
        title: 'Reset token sent',
        description: 'Please check your email for the reset token',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      setLoading(false);
      toast({
        title: 'Error',
        description: 'Failed to send reset token',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handlePin = async () => {
    try {
      setLoading(true);
      await verify(form.email, form.otp);
      setLoading(false);
      setStep(2)
      toast({
        title: 'Token verified',
        description: 'Please enter your new password',
        status: 'success',
        duration: 3000,
        isClosable: true,
      });

    } catch (error) {
      setLoading(false);
      toast({
        title: 'Error',
        description: 'Invalid token',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  async function saveNewPassword() {
    try {
      setLoading(true);
      await userResetChangePassword(password.new)
      setLoading(false);
      toast({
        title: 'Password updated',
        description: 'Your password has been successfully updated',
        status: 'success',
        isClosable: true,
      })
      // Redirect to login
      window.location.href = '/login'
    } catch (error) {
      setLoading(false);
      setError('Failed to update password')
    }
  }

  // methods
  const hasAnyEmptyValues = password.new === '' || password.confirm_new === '' ? true : false

  async function checkPasswords() {
    const newPasswordsAreEqual = password.new === password.confirm_new ? true : false
    const smallpass = password.new.length < 6 && (password.confirm_new?.length ?? 0) < 6 ? true : false

    if (smallpass) {
      setIsError({
        ...isError,
        ['new']: true,
        ['confirm_new']: true
      })
      setError(errorMessages.tooSmall)
    }

    if (!newPasswordsAreEqual) {
      setIsError({
        ...isError,
        ['new']: true,
        ['confirm_new']: true
      })
      setError(errorMessages.mismatch)
    }

    if (!hasAnyEmptyValues && newPasswordsAreEqual) {
      setIsError({
        ...isError,
        ['old']: false,
        ['new']: false,
        ['confirm_new']: false
      })
      setError(errorMessages.noError)
      await saveNewPassword()
    } else {
      return
    }
  }

  const RenderSteps = () => {
    if (step === 0) {
      return (
        <Flex
          zIndex='2'
          gap={'20px'}
          direction='column'
          w={{ base: "100%", md: "420px" }}
          maxW='100%'
          background='transparent'
          borderRadius='15px'
          mx={{ base: "auto", lg: "unset" }}
          me='auto'
          mb={{ base: "20px", md: "auto" }}>
          <Text>Enter your email address to receive a reset token</Text>
          <Flex direction={'column'} gap="20px" alignItems={'flex-start'}>
            <FormControl>
              <FormLabel>E-mail</FormLabel>
              <Input
                size={'lg'}
                type="email"
                placeholder="Ex: john@email.com"
                onChange={(e) => setForm({ ...form, email: e.target.value })}
              />
            </FormControl>
            <Button colorScheme={'blue'} onClick={askReset} isLoading={loading}>
              Send Reset Token
            </Button>
          </Flex>
        </Flex>
      )
    }

    if (step === 1) {
      return (
        <Flex
          zIndex='2'
          gap={'20px'}
          direction='column'
          w={{ base: "100%", md: "420px" }}
          maxW='100%'
          background='transparent'
          borderRadius='15px'
          mx={{ base: "auto", lg: "unset" }}
          me='auto'
          mb={{ base: "20px", md: "auto" }}>
          <Text>Enter the 6-digit token sent to your email</Text>
          <Flex direction={'column'} gap="20px" alignItems={'flex-start'}>
            <HStack>
              <PinInput otp onComplete={(e) => setForm({ ...form, otp: e })}>
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
                <PinInputField />
              </PinInput>
            </HStack>
            <Button colorScheme={'blue'} onClick={handlePin} isLoading={loading}>
              Verify Token
            </Button>
          </Flex>
        </Flex>
      )
    }

    if (step === 2) {
      return (
        <Flex
          zIndex='2'
          gap={'20px'}
          direction='column'
          w={{ base: "100%", md: "420px" }}
          maxW='100%'
          background='transparent'
          borderRadius='15px'
          mx={{ base: "auto", lg: "unset" }}
          me='auto'
          mb={{ base: "20px", md: "auto" }}>
          <Text>Enter your new password</Text>
          <Flex direction={'column'} gap="20px" alignItems={'flex-start'}>
            <Flex gap={2} direction={'column'}>
              {error && (
                <Alert status='error'>
                  <AlertIcon />
                  <Flex direction={'column'}>
                    <AlertTitle>Error</AlertTitle>
                    <AlertDescription>{error}</AlertDescription>
                  </Flex>
                </Alert>
              )}
              <FormControl id="new" mb={4}>
                <FormLabel>New Password</FormLabel>
                <InputGroup size='lg'>
                  <Input
                    errorBorderColor='tomato'
                    isInvalid={isError.new}
                    name='new'
                    type='password'
                    onChange={(e) => setPassword({ ...password, [e.target.name]: e.target.value })}
                  />
                </InputGroup>
              </FormControl>
              <FormControl id="confirm_new" mb={4}>
                <FormLabel>Confirm New Password</FormLabel>
                <InputGroup size='lg'>
                  <Input
                    errorBorderColor='tomato'
                    isInvalid={isError.confirm_new}
                    name='confirm_new'
                    type='password'
                    onChange={(e) => setPassword({ ...password, [e.target.name]: e.target.value })}
                  />
                </InputGroup>
              </FormControl>
            </Flex>
            <Button colorScheme={'blue'} onClick={checkPasswords} isLoading={loading}>
              Update Password
            </Button>
          </Flex>
        </Flex>
      )
    }
  }

  return (
    <Flex minH="100vh" direction={{ base: 'column', md: 'row' }}>
      {/* Aside with background image */}
      <Box
        flex="1"
        display={{ base: 'none', md: 'block' }}
        bgImage={bgImage}
        bgSize="cover"
        bgPosition="center"
        bgRepeat="no-repeat"
        position="relative"
        minH="100vh"
      >
        <Box position="absolute" top="6" left="8">
          <LogoIcon />
        </Box>
      </Box>
      <Flex flex="1" align="center" justify="center" bg="white">
        <Box w="full" maxW="md" p={8}>
          <Heading as="h2" size="lg" mb={2} textAlign="left">
            Reset Password
          </Heading>
          <Text color="gray.500" mb={6}>
            Follow the steps to reset your password
          </Text>
          {RenderSteps()}
        </Box>
      </Flex>
    </Flex>
  )
}

export default ResetPassword 